import type { GameTextReference, Text } from "@shared/types";
import { css, html, LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";

import { Events } from "../constants";

const TYPING_SPEED = 15; // in milliseconds
const WAIT_TIME = 5e3;

@customElement("animated-text")
export class AnimatedText extends LitElement {
	public static styles = css`
		.content {
			flex-grow: 1;
			/* overflow: auto; */
			margin-top: 10px;
			padding: 0 10px;
		}

		.content p {
			margin: 0 0 10px 0;
		}

		.content p:last-of-type {
			margin: 0;
		}

		.speaker.character {
			color: #7bf0e2;
		}

		.speaker.story {
			color: #c5dd71;
		}
	`;

	@state() private text: Text[] = [];
	@state() private visibleContent: string = "";
	@state() private speaker: string = "player";

	private abortController?: AbortController;
	private arrayIndex = 0;

	public connectedCallback() {
		super.connectedCallback();
		window.addEventListener(Events.SKIP_ANIMATION, this.skipAnimation.bind(this));
		window.addEventListener<any>(Events.TEXT_CHANGE, this.onTextChange.bind(this));
	}

	public disconnectedCallback() {
		super.disconnectedCallback();
		window.removeEventListener(Events.SKIP_ANIMATION, this.skipAnimation.bind(this));
		window.removeEventListener<any>(Events.TEXT_CHANGE, this.onTextChange.bind(this));
	}

	protected render() {
		return html`<div class="content speaker ${this.speaker}">${this.visibleContent}</div>`;
	}

	private onTextChange(event: CustomEvent<GameTextReference | undefined>) {
		if (!(event instanceof CustomEvent)) throw new Error("ANIMATED_TEXT(ON_TEXT_CHANGE): Invalid event type");

		if (this.abortController) this.abortController.abort(); // cancel all running animations
		const handleFinish = () => {
			console.debug("ANIMATED_TEXT: Animation finished as expected");
			this.handleFinish();
		};

		// Reset states
		this.visibleContent = "";
		this.speaker = "player";
		this.arrayIndex = 0;

		switch (event.detail?.type) {
			case "animated":
				this.text = event.detail.text;
				void this.startAnimation()
					.then(handleFinish)
					.catch(() => void 0);
				break;
			case "static":
				this.text = event.detail.text;
				this.visibleContent = this.text[0].content;
				this.speaker = this.text[0].speaker;
				break;
			default:
				console.debug("ANIMATED_TEXT: No text to show;");
				this.text = [];
				break;
		}
	}

	/**
	 * Skips the animation and shows the full text
	 * @returns
	 */
	private skipAnimation() {
		if (this.abortController) this.abortController.abort();
		if (!this.text[this.arrayIndex]) {
			console.debug("ANIMATED_TEXT(SKIP_ANIMATION): No text to show - 1;", `index: ${this.arrayIndex};Length:${this.text.length}`, this.text);
			return this.handleFinish();
		}

		let text = this.text[this.arrayIndex];
		if (text?.content === this.visibleContent) {
			this.arrayIndex++;
			text = this.text[this.arrayIndex];
		}

		if (!text) {
			console.debug("ANIMATED_TEXT(SKIP_ANIMATION): No text to show - 2;", `index: ${this.arrayIndex};Length:${this.text.length}`, this.text);
			return this.handleFinish();
		}

		this.visibleContent = text.content;
		this.speaker = text.speaker;

		this.arrayIndex++;
		if (!this.text[this.arrayIndex]) {
			console.debug("ANIMATED_TEXT(SKIP_ANIMATION): No text to show - 3;", `index: ${this.arrayIndex};Length:${this.text.length}`, this.text);
			return this.handleFinish();
		}
	}

	private handleFinish() {
		this.abortController = undefined;
		window.dispatchEvent(new CustomEvent(Events.ANIMATION_FINISHED));
	}

	/**
	 * Starts the typewriter animation
	 * @returns
	 */
	private startAnimation() {
		const abortController = new AbortController();
		this.abortController = abortController;

		let aborted = false;

		return new Promise(async (res, rej) => {
			if (!this.text.length) return rej(new Error("No text to animate"));

			function handleError(err: any) {
				aborted = true;
				rej(err);
			}

			for await (const paragraph of this.text) {
				if (aborted) return;

				this.visibleContent = "";
				this.speaker = paragraph.speaker;

				await this.animateText(paragraph.content, abortController.signal).catch(handleError);
				if (this.arrayIndex + 1 < this.text.length) await this.timeout(WAIT_TIME, abortController.signal).catch(handleError);
				if (!aborted) this.arrayIndex++;
			}

			res(true);
		});
	}

	/**
	 * Adds typewriter effect to a string of text
	 * @param text The text to animate
	 * @param abort The abort controller signal
	 * @returns
	 */
	private async animateText(text: string, abort: AbortSignal) {
		let aborted = false;

		return new Promise(async (res, rej) => {
			abort.addEventListener("abort", () => {
				aborted = true;
				rej(new Error("Animation aborted"));
			});

			for await (const character of text.split("")) {
				if (aborted) return;

				this.visibleContent += character;
				await new Promise((resolve) => setTimeout(resolve, TYPING_SPEED));
			}

			res(true);
		});
	}

	/**
	 * Waits for a set duration, listens to abortion signal to cancel if needed
	 * @param duration The duration to wait
	 * @param abort The abortion signal
	 * @returns
	 */
	private timeout(duration: number, abort: AbortSignal) {
		return new Promise((res, rej) => {
			const timeout = setTimeout(() => res(true), duration);

			abort.addEventListener("abort", () => {
				clearTimeout(timeout);
				rej(new Error("Animation aborted"));
			});
		});
	}
}
